import React, {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  ID,
  ISO8601DateTime,
  Template,
  TimeZone,
  type ExtractByComponentType,
} from "../../types";
import { LoadingScreen } from "../screens/LoadingScreen";
import { api } from "../helpers/api";
import { useSecureToken, useStatus } from "./StatusProvider";
import { useDeviceId } from "./DeviceProvider";
import { errorReporting } from "../helpers/errorReporting";
import { config } from "../config";
import { useTranslation } from "react-i18next";
import { ThemeResponse, useSetTheme } from "./ThemeProvider";
import { isEqual } from "lodash";
import { usePrevious } from "../hooks/usePrevious";

type NextMatch = {
  id: ID;
  title: string;
  startsAt: ISO8601DateTime;
  opponentBadgeUrl?: string;
  opponentBadgePosition: "left" | "right";
};

export interface ScheduleEvent {
  id: ID;
  title: string;
  startsAt: ISO8601DateTime;
  endsAt: ISO8601DateTime;
  location?: string;
  allDay?: boolean;
}

interface MultiDaySchedule {
  type: "MultiDaySchedule";
  date: ISO8601DateTime;
  events: ScheduleEvent[];
}

interface MultiColumnSchedule {
  type: "MultiColumnSchedule";
  title: string;
  events: ScheduleEvent[];
  timeZone?: TimeZone;
  disableLoop?: boolean;
  nextDayEvents?: ScheduleEvent[];
}

export type ScreenMediaContent =
  | {
      type: "video";
      url?: string;
    }
  | {
      type: "image";
      url?: string;
      duration: number;
    }
  | {
      type: "youtube";
      youtubeId: string;
    };

export type MediaContentData = ScreenMediaContent[];

interface ScheduleConfigData {
  events: ScheduleEvent[];
  timeZone?: TimeZone;
  disableLoop?: boolean;
  nextDayEvents?: ScheduleEvent[];
}

export interface MultiDayScheduleConfigData {
  events: MultiDaySchedule[];
  timeZone?: TimeZone;
  totalScheduleDays: number;
  disableLoop?: boolean;
}

export interface MultiColumnScheduleConfigData {
  events: MultiColumnSchedule[];
}

export type CardsContentData = {
  cards: { title?: string; text: string; imageUrl?: string }[];
};

export type NutritionMenuItemData = {
  id: string;
  name: string;
  description: string;
  macros: {
    cho: number;
    pro: number;
    fat: number;
    kcal: number;
  };
  hasAddOn: boolean;
  imageUrl: string;
};

export type NutritionDiningStationData = {
  diningStation: {
    id: string;
    name: string;
    position: number;
    menuItemAppearances: {
      id: string;
      position: number;
      menuItem: NutritionMenuItemData;
    }[];
  };
  includeImages: boolean;
  includeDescriptions: boolean;
  includeMacros: boolean;
};

export type NutritionMenuData = {
  menu: {
    id: string;
    name: string;
    mealMenuDiningStations: NutritionDiningStationData["diningStation"][];
  };
  includeImages: boolean;
  includeDescriptions: boolean;
  includeMacros: boolean;
};

export type StrengthWorkoutExercise = {
  label: string;
  name: string;
  instruction: string | null;
  prescription: string[];
  supersetGroup: string | null;
  supersetPosition: number | null;
};

export type StrengthWorkoutSection = {
  id: string;
  name: string;
  instruction: string | null;
  exercises: StrengthWorkoutExercise[];
};

export type StrengthWorkoutSelection = {
  team: { id: string; name: string };
  blockName: string;
  workout: { name: string; cycleLabel: string; sessionLabel: string };
  quote: string | null;
  highlightSupersets: boolean;
  pageDurationSeconds: number | null;
  pages: StrengthWorkoutSection[][];
};

export type StrengthWorkoutData = {
  selection: StrengthWorkoutSelection | null;
};

export type ConfigurationComponent = { name: string } & (
  | {
      componentType: "schedule";
      data: ScheduleConfigData;
    }
  | {
      componentType: "multi_day_schedule";
      data: MultiDayScheduleConfigData;
    }
  | {
      componentType: "multi_column_schedule";
      data: MultiColumnScheduleConfigData;
    }
  | {
      componentType: "cards";
      data: CardsContentData;
    }
  | {
      componentType: "media";
      data: MediaContentData;
    }
  | {
      componentType: "nutrition_menu";
      data: NutritionMenuData | null;
    }
  | {
      componentType: "nutrition_dining_station";
      data: NutritionDiningStationData | null;
    }
  | {
      componentType: "strength_workout";
      data: StrengthWorkoutData | null;
    }
  | {
      componentType: "weather";
      data: WeatherData;
    }
);

interface WeatherData {
  dailyBreakdown: WeatherResponse[];
  hourlyBreakdown: WeatherResponse[];
}

export type WeatherResponse = {
  date: string;
  location: string;
  weather: {
    temperature: string;
    weatherCondition: string;
    icon: string;
    pop: number;
    wind: number;
  };
};

type LocationResponse = {
  time: string;
  temperature: string;
  weatherCondition: string;
  icon: string;
  timezone?: string;
}[];

type ActiveConfigurationResponse = {
  status: "active";
  theme: ThemeResponse;
  showWeather: boolean;
  hideSchedule?: boolean;
  location: LocationResponse | null;
  template: Template;
  nextMatch: NextMatch | null;
  cityName: string | null;
  components: Array<ConfigurationComponent>;
};

type ConfigurationResponse =
  | ActiveConfigurationResponse
  | { status: "not-found" };

const Context = createContext<ConfigurationResponse>(null!);

export const ConfigProvider = ({
  children,
  configId,
}: PropsWithChildren & {
  configId: ID;
}) => {
  const { t } = useTranslation("ConfigProvider");
  const secureToken = useSecureToken({ strict: false });
  const deviceId = useDeviceId();
  const setTheme = useSetTheme();
  const status = useStatus();
  const prevConfigId = usePrevious(configId);

  if (status.status === "error" || status.status === "inactive") {
    throw new Error(
      `ConfigProvider: invalid status - ${JSON.stringify(status)}`,
    );
  }

  // Cache array of all configs fetched
  const [configs, setConfigs] = useState<
    (ConfigurationResponse & { configId: ID })[]
  >([]);

  // Grab active config from the cache
  const activeConfig = configs.find((c) => c.configId === configId);

  // Update theme when active config changes
  useEffect(() => {
    if (activeConfig?.status === "active") {
      setTheme(activeConfig.theme);
    }
  }, [activeConfig, setTheme]);

  // Fetch config and update cache on configId change
  // Set interval to refetch config every X seconds
  useEffect(() => {
    const fetchConfig = async (id: ID) => {
      const { data } = await (
        status.status === "hard_coded"
          ? status.type === "device"
            ? api(status.region).get<ConfigurationResponse>(
                `/screens/virtual_device/${status.urlCode}/playlist/${id}`,
              )
            : status.type === "playlist"
              ? api(status.region).get<ConfigurationResponse>(
                  `/screens/${status.urlCode}/hardcoded_playlist/${id}`,
                )
              : api(status.region).get<ConfigurationResponse>(
                  `/screens/${status.urlCode}/hardcoded_config/${id}`,
                )
          : api(status.region, secureToken).get<ConfigurationResponse>(
              `/screens/${deviceId}/playlist/${id}`,
            )
      ).catch((err) => {
        errorReporting.sendError(err);
        return { data: null };
      });

      if (data != null) {
        const newConfig = { ...data, configId: id };
        const existing = configs.find((c) => c.configId === id);

        if (existing != null && !isEqual(existing, newConfig)) {
          setConfigs(configs.map((c) => (c.configId === id ? newConfig : c)));
        } else if (existing == null) {
          setConfigs([...configs, newConfig]);
        }
      }
    };

    // refresh instantly if config id changed
    if (prevConfigId !== configId) {
      fetchConfig(configId);
    }

    // refresh every X seconds
    const interval = setInterval(
      () => fetchConfig(configId),
      config.configPollingRate,
    );

    return () => clearInterval(interval);
  }, [configId, deviceId, secureToken, status, configs, prevConfigId]);

  if (activeConfig == null) {
    return <LoadingScreen message={t("Loading configuration")} />;
  }

  return <Context.Provider value={activeConfig}>{children}</Context.Provider>;
};

export const useConfig = () => {
  const ctx = useContext(Context);
  if (ctx == null) {
    throw new Error("useActiveConfig: context not ready");
  }

  return ctx;
};

export const useShowWeather = () => {
  const config = useConfig();
  return config.status === "active" ? config.showWeather : false;
};

export const useHideSchedule = () => {
  const config = useConfig();
  return config.status === "active" ? (config.hideSchedule ?? false) : false;
};

export const useLocation = () => {
  const config = useConfig();
  return config.status === "active" ? config.location : undefined;
};

export const useNextMatch = () => {
  const config = useConfig();
  return config.status === "active" ? config.nextMatch : undefined;
};

export const useCityName = () => {
  const config = useConfig();
  return config.status === "active" ? config.cityName : undefined;
};

export const useTemplate = () => {
  const config = useConfig();
  return config.status === "active" ? config.template : undefined;
};

export function useComponent<
  T extends ActiveConfigurationResponse["components"][number]["componentType"],
  DataType extends ExtractByComponentType<
    T,
    ActiveConfigurationResponse["components"][number]
  >["data"],
>(componentType: T, name: string) {
  const config = useContext(Context);
  const components = config?.status === "active" ? config.components : [];
  return components.find(
    (c) => c.componentType === componentType && c.name === name,
  )?.data as DataType | undefined;
}
